/**
 * Blender → GLB hero loader (Château slot).
 * SPA-safe: rejects HTML fallbacks from Cloudflare Pages.
 */
import { Group } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

export type GlbHeroHandle = {
  group: Group;
  ready: boolean;
  dispose: () => void;
};

export type GlbHeroSpec = {
  enabled?: boolean;
  path: string;
  rotationY?: number;
  scale?: number;
  /** Optional local offset in meters (ENU) */
  offset?: [number, number, number];
  attribution?: string;
};

export type HeroAssetManifest = {
  id: string;
  glb?: GlbHeroSpec;
  splat?: {
    enabled?: boolean;
    format?: "ksplat" | "splat" | "ply";
    path: string;
    position?: [number, number, number];
    rotation?: [number, number, number, number];
    scale?: [number, number, number];
    attribution?: string;
  };
  attribution?: string;
};

export async function loadHeroManifest(
  url: string,
): Promise<HeroAssetManifest | null> {
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) return null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("text/html")) return null;
    return (await res.json()) as HeroAssetManifest;
  } catch {
    return null;
  }
}

async function glbAssetExists(path: string): Promise<boolean> {
  try {
    const res = await fetch(path, {
      headers: { Range: "bytes=0-15" },
      cache: "no-cache",
    });
    if (!res.ok && res.status !== 206) return false;
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("text/html") || ct.includes("application/json")) return false;
    const buf = new Uint8Array(await res.arrayBuffer());
    if (buf.length < 4) return false;
    // glTF binary magic "glTF"
    if (buf[0] === 0x67 && buf[1] === 0x6c && buf[2] === 0x54 && buf[3] === 0x46) {
      return true;
    }
    // Reject HTML
    if (buf[0] === 0x3c) return false;
    // Some CDNs strip magic in range responses — allow octet-stream / model
    if (ct.includes("model") || ct.includes("octet-stream") || ct.includes("gltf")) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function tryLoadGlbHero(
  spec: GlbHeroSpec,
): Promise<GlbHeroHandle | null> {
  if (spec.enabled === false) return null;
  if (!(await glbAssetExists(spec.path))) return null;

  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(spec.path);
  const root = new Group();
  root.name = "chateau-frontenac-glb-hero";
  root.userData = {
    kind: "glb-hero",
    id: "chateau-frontenac",
    attribution: spec.attribution ?? "Blender GLB hero",
  };
  root.add(gltf.scene);
  root.rotation.y = spec.rotationY ?? 0;
  root.scale.setScalar(spec.scale ?? 1);
  if (spec.offset) {
    root.position.set(spec.offset[0], spec.offset[1], spec.offset[2]);
  }

  return {
    group: root,
    ready: true,
    dispose() {
      root.traverse((obj) => {
        const mesh = obj as { geometry?: { dispose: () => void }; material?: { dispose: () => void } | Array<{ dispose: () => void }> };
        mesh.geometry?.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose());
        else mesh.material?.dispose();
      });
      root.clear();
    },
  };
}
